import type { AssetManifest } from '@kenocyber/shared';
import type Phaser from 'phaser';
import { memo, useEffect, useRef } from 'react';
import type { GameBridge, HuntSessionMessage } from './bridge';
import { createKenocyberGame, type KenocyberGame } from './create-game';

interface PhaserHostProps {
  readonly bridge: GameBridge;
  readonly manifest: AssetManifest;
  readonly debug: boolean;
  readonly bossFast: boolean;
  /** DEV ONLY - very high cadence, still drawn as discrete projectiles. */
  readonly rapidFire: boolean;
  /** Server-authorised session; the host mounts only once it exists. */
  readonly session: HuntSessionMessage;
}

/**
 * Owns the single Phaser.Game instance.
 *
 * The component is memoised and its effect depends only on stable references,
 * so React re-renders NEVER recreate the canvas - the game boots once and is
 * destroyed only on unmount. The arena is a fixed logical resolution scaled to
 * fit, keeping the simulation deterministic regardless of window size.
 *
 * WHAT the game is - renderer, canvas, transparency, scale, the three scenes
 * and their order - lives in `create-game.ts` and is shared with the visual
 * benchmark. This component owns only WHEN one exists: exactly one per mounted
 * host, never rebuilt underneath the player.
 */
function PhaserHostImpl({
  bridge,
  manifest,
  debug,
  bossFast,
  rapidFire,
  session,
}: PhaserHostProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);

  // `session` is intentionally not a dependency: it seeds the FIRST run only.
  // Later sessions (checkpoint reconciliation, restart, point allocation)
  // arrive through the bridge and are adopted in place, so the game and its
  // canvas are never destroyed and rebuilt underneath the player.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    const container = containerRef.current;
    if (!container || gameRef.current) {
      return;
    }

    const hosted: KenocyberGame = createKenocyberGame({
      container,
      bridge,
      manifest,
      debug,
      bossFast,
      rapidFire,
      session,
    });
    gameRef.current = hosted.game;

    return () => {
      gameRef.current = null;
      // The observer, the inspection handle and the game itself, in the order
      // the factory established. Nothing about teardown is restated here.
      hosted.destroy();
    };
  }, [bridge, manifest, debug, bossFast, rapidFire]);

  return <div ref={containerRef} className="phaser-host" data-testid="phaser-host" />;
}

export const PhaserHost = memo(PhaserHostImpl);
